// Событие, которое браузеры на базе Chromium отправляют, когда сайт можно установить как приложение.
interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>
    userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string;}>
}

declare global {
    interface WindowEventMap {
        beforeinstallprompt: BeforeInstallPromptEvent
    }
}

type Listener = () => void

// Сервис установки сайта как PWA приложения.
// Событие beforeinstallprompt может прийти до монтирования React, поэтому сервис начинает слушать его при импорте.
export class PwaInstallService {

    // Отложенное событие установки. null: установка недоступна или приложение уже установлено.
    private static deferredPrompt: BeforeInstallPromptEvent | null = null

    private static listeners = new Set<Listener>()

    private static isListening = false

    // Начать слушать события установки. Повторные вызовы игнорируются.
    static init(): void {
        if (this.isListening || typeof window === 'undefined') {
            return
        }
        this.isListening = true

        window.addEventListener('beforeinstallprompt', event => {
            // Отключаем стандартное мини-окно браузера: показ установки управляется кнопкой.
            event.preventDefault()
            this.deferredPrompt = event
            this.notify()
        })

        window.addEventListener('appinstalled', () => {
            this.deferredPrompt = null
            this.notify()
        })
    }

    // Можно ли сейчас показать установку приложения.
    static get canInstall(): boolean {
        return this.deferredPrompt !== null
    }

    // Показать системное окно установки приложения.
    // Событие можно использовать только один раз, независимо от выбора пользователя.
    static async install(): Promise<boolean> {
        const promptEvent = this.deferredPrompt
        if (!promptEvent) {
            return false
        }
        this.deferredPrompt = null
        this.notify()
        try {
            await promptEvent.prompt()
            const choice = await promptEvent.userChoice
            return choice.outcome === 'accepted'
        } catch (error) {
            console.error('[PwaInstallService] Install prompt error: ', error)
            return false
        }
    }

    // Подписаться на изменение доступности установки. Возвращает функцию отписки.
    // Стрелочная функция, чтобы передавать в useSyncExternalStore без bind.
    static subscribe = (listener: Listener): () => void => {
        PwaInstallService.listeners.add(listener)
        return () => {
            PwaInstallService.listeners.delete(listener)
        }
    }

    // Получить текущее состояние для useSyncExternalStore.
    static getSnapshot = (): boolean => PwaInstallService.canInstall

    private static notify(): void {
        this.listeners.forEach(listener => {
            listener()
        })
    }
}
